import React, { useEffect, useState } from "react";
import {
  Table,
  Title,
  Paper,
  Badge,
  Stack,
  Text,
  Group,
  ScrollArea,
  Skeleton,
  Pagination,
} from "@mantine/core";
import { getSubscribers } from "../../../services/newsletterService";
import { ISubscriber } from "../../../lib/types/Newsletter";
import "./styles.scss";

const Newsletter: React.FC = () => {
  const [subscribers, setSubscribers] = useState<ISubscriber[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  useEffect(() => {
    loadSubscribers();
  }, [page]);

  async function loadSubscribers() {
    setLoading(true);
    try {
      const result = await getSubscribers(page, limit);
      if (result && result.data) {
        setSubscribers(result.data);
        setTotalPages(Math.ceil(result.totalRecords / result.limit));
      }
    } catch (error) {
      console.error("Failed to load subscribers:", error);
    }
    setLoading(false);
  }

  const rows = subscribers.map((subscriber) => (
    <Table.Tr key={subscriber._id}>
      <Table.Td>
        <Text fw={500}>{subscriber.email}</Text>
      </Table.Td>
      <Table.Td>
        <Badge
          color={subscriber.isSubscribed ? "teal" : "red"}
          variant="light"
          radius="sm"
        >
          {subscriber.isSubscribed ? "Active" : "Unsubscribed"}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {new Date(subscriber.createdAt).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Text>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <div className="newsletter-container">
      <Stack gap="xl">
        <div>
          <Title order={2} mb="xs">
            Newsletter Subscribers
          </Title>
          <Text c="dimmed">
            Manage your newsletter audience and view subscription status
          </Text>
        </div>

        <Paper p="md" radius="md" shadow="xs" withBorder>
          {loading ? (
            <Stack gap="xs">
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
              <Skeleton height={50} />
            </Stack>
          ) : subscribers.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl">
              No subscribers found
            </Text>
          ) : (
            <ScrollArea>
              <Table highlightOnHover striped verticalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Email Address</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Subscribed On</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{rows}</Table.Tbody>
              </Table>
            </ScrollArea>
          )}

          {totalPages > 1 && (
            <Group justify="center" mt="xl">
              <Pagination
                value={page}
                onChange={setPage}
                total={totalPages}
                size="sm"
                radius="md"
              />
            </Group>
          )}
        </Paper>
      </Stack>
    </div>
  );
};

export default Newsletter;
